import * as PopperJS from "@popperjs/core";
import classNames from "classnames";
import IMask from "imask";
import React, { FC, FocusEventHandler, useCallback, useRef } from "react";
import {
  Day,
  GetButtonPropsOptions,
  Calendar as UseCalendarReturnProps,
} from "../../../hooks/use-calendar/use-calendar";
import { ButtonProps } from "../../button";
import { IconCalendar } from "../../icons";
import { InputProps, MaskedInput, MaskedInputProps } from "../../input";
import { Popover, PopoverContent } from "../../popover";
import { PopoverProps } from "../../popover/popover.types";
import { CSS_CLASSES } from "../date-time-input-css-classes";
import { Calendar, CalendarOwnProps } from "./calendar";

export interface DateInputOwnProps {
  id?: string;
  className?: string;
  datePopoverClassName?: string;
  datePopoverContentClassName?: string;
  dateInputWrapperClassName?: string;
  dateInputClassName?: string;
  dateInputBorderClassName?: string;
  datePlaceholder?: string;
  datePicker?: boolean;
  maskOptions?: any;
  name?: string;
  required?: boolean;
  disabled?: boolean;
  autoComplete?: InputProps["autoComplete"];
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  withTime: boolean;
  showDateClearButton?: boolean;
  alwaysShowDateClearButton?: boolean;
  onDateClear?: ButtonProps["onClick"];
  placement?: PopperJS.Placement;
  flip?: boolean;
  modal?: boolean;
  color?: MaskedInputProps["color"];
}

export type DateInputToCalenderPassThroughProps = CalendarOwnProps;

export type CalendarToDateInputProps = Omit<UseCalendarReturnProps, "times" | "selectTime">;

export type DateInputToPopoverPassthroughProps = Pick<PopoverProps, "usePortal">;

export type DateInputProps = DateInputOwnProps &
  CalendarToDateInputProps &
  DateInputToCalenderPassThroughProps &
  DateInputToPopoverPassthroughProps;

const DATE_MASK = {
  mask: "MM/DD/YYYY",
  lazy: true,
  blocks: {
    MM: {
      mask: IMask.MaskedRange,
      from: 1,
      to: 12,
      placeholderChar: "M",
    },
    DD: {
      mask: IMask.MaskedRange,
      from: 1,
      to: 31,
      placeholderChar: "D",
    },
    YYYY: {
      mask: IMask.MaskedRange,
      from: 1900,
      to: 9999,
      placeholderChar: "Y",
    },
  },
};

export const DateInput: FC<DateInputProps> = ({
  /* DatePickerUIOwn props */
  id,
  className,
  datePlaceholder = "mm/dd/yyyy",
  datePicker = false,
  maskOptions,
  name,
  required,
  disabled,
  autoComplete,
  onFocus,
  onBlur,
  withTime,
  selectsStart,
  selectsEnd,
  dateInputWrapperClassName,
  dateInputClassName,
  dateInputBorderClassName,
  datePopoverClassName,
  datePopoverContentClassName,
  showOutsideMonthDates,
  allowSelectOutsideMonthDates,
  showDateClearButton,
  alwaysShowDateClearButton,
  onDateClear,
  placement = "bottom-start",
  flip = true,
  modal = false,
  color = "interactive",
  /** CalendarToDatePicker Props */
  isOpen,
  months,
  getInputProps,
  openCalendar,
  getDayProps,
  getGridProps,
  getPreviousMonthProps,
  getNextMonthProps,
  getPreviousYearProps,
  getNextYearProps,
  closeCalendar,
  /** Popover Pass-through Props */
  usePortal,
}) => {
  const popoverContentClass = classNames(
    {
      [CSS_CLASSES.DATE_TIME_INPUT_POPOVER]: true,
      [CSS_CLASSES.DATE_TIME_INPUT_POPOVER_CLOSED]: !isOpen && !modal,
    },
    datePopoverContentClassName
  );

  const closePopoverContentRef = useRef(null);
  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.code === "Enter" || event.code === "Escape") {
      closePopoverContentRef.current?.close();
    }
  };

  const renderInput = () => {
    const maskedInputProps = {
      unmask: false,
      maskOptions: maskOptions ? maskOptions : DATE_MASK,
      ...getInputProps({ onFocus, onKeyDown, autoComplete }),
    };

    return (
      <MaskedInput
        id={id}
        name={name}
        required={required}
        disabled={disabled}
        formField={false}
        endEnhancer={({ focused }) => (
          <IconCalendar
            color={focused ? color : "gray"}
            className={disabled ? "cursor-not-allowed" : undefined}
            onClick={
              disabled
                ? undefined
                : () => {
                    maskedInputProps?.inputRef?.current?.focus();
                    datePicker && (!isOpen ? openCalendar() : closeCalendar());
                  }
            }
          />
        )}
        placeholder={datePlaceholder}
        onFocus={onFocus}
        onBlur={onBlur}
        inputClassName={dateInputClassName}
        inputBorderClassName={dateInputBorderClassName}
        className={dateInputWrapperClassName}
        showClearButton={showDateClearButton}
        alwaysShowClearButton={alwaysShowDateClearButton}
        onClearButtonClick={onDateClear}
        color={color}
        {...maskedInputProps}
      />
    );
  };

  const rootDateClassName = classNames(
    { [CSS_CLASSES.DATE_TIME_INPUT_DATE]: true, [CSS_CLASSES.DATE_TIME_INPUT_WITH_TIME]: withTime },
    className
  );

  const handlePopoverClose: PopoverProps["onInteraction"] = useCallback(
    (nextOpenState) => {
      if (nextOpenState === false) {
        closeCalendar();
      }
    },
    [closeCalendar]
  );

  return (
    <div className={rootDateClassName}>
      {!datePicker ? (
        renderInput()
      ) : (
        <Popover
          open={modal ? isOpen : undefined}
          disabled={disabled}
          onInteraction={modal ? handlePopoverClose : undefined}
          showBackdrop={modal ? true : undefined}
          backdropClassName={modal ? "opacity-0" : undefined}
          usePortal={usePortal}
          placement={placement}
          modifiers={[
            {
              name: "flip",
              enabled: flip,
            },
          ]}
          triggerInteraction="click"
          onClose={closeCalendar}
          className={datePopoverClassName}
          content={(popoverContentProps) => {
            closePopoverContentRef.current = popoverContentProps;
            const getPopoverClosingDayProps = (day: Day, options?: GetButtonPropsOptions) => {
              const onClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                popoverContentProps?.close();
                options?.onClick(event);
              };
              return getDayProps(day, { ...options, onClick });
            };
            return (
              <PopoverContent {...popoverContentProps} className={popoverContentClass}>
                <Calendar
                  months={months}
                  getDayProps={getPopoverClosingDayProps}
                  getGridProps={getGridProps}
                  getPreviousMonthProps={getPreviousMonthProps}
                  getNextMonthProps={getNextMonthProps}
                  getPreviousYearProps={getPreviousYearProps}
                  getNextYearProps={getNextYearProps}
                  selectsStart={selectsStart}
                  selectsEnd={selectsEnd}
                  showOutsideMonthDates={showOutsideMonthDates}
                  allowSelectOutsideMonthDates={allowSelectOutsideMonthDates}
                  color={color}
                />
              </PopoverContent>
            );
          }}
        >
          {renderInput()}
        </Popover>
      )}
    </div>
  );
};
